import { useEffect, useState } from 'react';
import Dialog from '#lib/components/dialog.jsx';
import { Icon } from '@iconify-icon/react';
import { AddRowButton } from '#@itinerary/planner/row.jsx';
import Input from '#lib/components/input.jsx';
import HTMLEditor from '#components/html-editor.jsx';
import { addClasses, cn } from '#lib/helpers/class.js';
import FloatingTooltip from '#components/tooltip.jsx';
import { compareDate } from '#lib/helpers/date.js';
import { useTasks } from './use-tasks.js';
import Button from '#lib/components/button.jsx';

export default function TasksModal(props) {
    const { isOpen, onClose, tasks, setTasks, taskName, id } = props;
    const [state, setState] = useState(tasks);
    // If tasks are loaded and taskName is provided from URL, set the selected task index
    const [selectedTaskIndex, setSelectedTaskIndex] = useState(
        taskName ? state?.findIndex((task) => task.name === taskName) : 0
    );
    const [tasksLoaded, setTasksLoaded] = useState(false);
    useEffect(() => {
        if (state && state.length > 0 && !tasksLoaded) {
            setSelectedTaskIndex(taskName ? state?.findIndex((task) => task.name === taskName) : 0);
            setTasksLoaded(true);
        }
    }, [state]);

    const saveMutation = useTasks(onClose, setTasks);

    const onChange = (field, value) => {
        const updated = structuredClone(state);
        updated[selectedTaskIndex][field] = value;
        setState(updated);
    };

    const selectedTask = state?.[selectedTaskIndex];

    let Detail;
    if (selectedTask) {
        Detail = (
            <TaskDetail
                tasks={state}
                setTasks={setState}
                selectedTaskIndex={selectedTaskIndex}
                selectedTask={selectedTask}
                onChange={onChange}
            />
        );
    }

    return (
        <Dialog
            isOpen={isOpen}
            onClose={() => {
                setSelectedTaskIndex(0);
                onClose();
            }}
        >
            <Dialog.Panel className="flex flex-col space-y-4 w-full h-full max-h-[80%] lg:w-2/3">
                <div className="pr-4">
                    <Dialog.CloseButton />
                    <b className="text-2xl">{_t.edit.tasks()}</b>
                    <p className="pr-2 mt-1 text-neutral-700 text-sm">{_t.edit.tasksDescription()}</p>
                </div>
                <div className="w-full h-full flex items-stretch gap-1 border border-neutral-300 rounded overflow-hidden">
                    <div className="w-1/3 pl-2 p-1 overflow-auto">
                        <div className="flex flex-col divide-y divide-t-neutral-400">
                            {state?.map((task, i) => {
                                const inputDate = task.dueDate;
                                const { isExpired, isExpiring } = compareDate(inputDate);

                                return (
                                    <div
                                        key={i}
                                        className={
                                            'p-2 flex justify-between items-center cursor-pointer hover:bg-primary-50 hover:text-primary-600'
                                        }
                                        onClick={() => setSelectedTaskIndex(i)}
                                    >
                                        <span
                                            className={cn(
                                                'flex gap-2 items-center',
                                                i === selectedTaskIndex && 'text-primary-600'
                                            )}
                                        >
                                            {!task.done && isExpiring && (
                                                <FloatingTooltip
                                                    content={_t.crud.itinerary.headers.dueTasks()}
                                                    placement="top"
                                                >
                                                    <Icon
                                                        icon="ic:round-error-outline"
                                                        width="1.2rem"
                                                        height="1.2rem"
                                                        className="text-amber-500 pt-2"
                                                    />
                                                </FloatingTooltip>
                                            )}
                                            {!task.done && isExpired && (
                                                <FloatingTooltip
                                                    content={_t.crud.itinerary.headers.expiredTasks()}
                                                    placement="top"
                                                >
                                                    <Icon
                                                        icon="ic:round-error-outline"
                                                        width="1.2rem"
                                                        height="1.2rem"
                                                        className="text-red-500 pt-2"
                                                    />
                                                </FloatingTooltip>
                                            )}
                                            {task.done && (
                                                <FloatingTooltip
                                                    content={_t.crud.itinerary.headers.taskDone()}
                                                    placement="top"
                                                >
                                                    <Icon
                                                        icon="zondicons:checkmark"
                                                        width="1.1rem"
                                                        height="1.1rem"
                                                        className="text-primary-500 pt-2"
                                                    />
                                                </FloatingTooltip>
                                            )}
                                            <span className="py-1">{task.name}</span>
                                        </span>
                                        <div className="flex gap-2">
                                            <FloatingTooltip content={_t.global.delete()}>
                                                <Icon
                                                    icon="ph:trash"
                                                    width="1.2rem"
                                                    height="1.2rem"
                                                    className="cursor-pointer text-neutral-800 hover:text-primary-600"
                                                    onClick={() => {
                                                        const updated = structuredClone(state);
                                                        updated.splice(i, 1);
                                                        setState(updated);
                                                    }}
                                                />
                                            </FloatingTooltip>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <AddRowButton
                            onClick={() => {
                                const updated = structuredClone(state);
                                updated.push({
                                    name: `Task ${updated.length + 1}`,
                                    done: false,
                                    description: '',
                                    dueDate: ''
                                });
                                setState(updated);
                                setSelectedTaskIndex(updated.length - 1);
                            }}
                        />
                    </div>

                    {Detail ? (
                        Detail
                    ) : (
                        <div className="w-2/3 h-full bg-neutral-100 relative">
                            <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center">
                                <p className="w-full text-center text-neutral-500 italic">
                                    {_t.edit.taskPlaceholder()}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                <Button
                    className={addClasses('w-fit self-end')}
                    look="white"
                    onClick={() => saveMutation.mutate({ tasks: state, id })}
                    busy={saveMutation.isLoading}
                >
                    {_t.global.save()}
                </Button>
            </Dialog.Panel>
        </Dialog>
    );
}

function TaskDetail(props) {
    const { tasks, setTasks, selectedTaskIndex, selectedTask, onChange } = props;
    return (
        <div className="w-2/3 flex flex-col gap-2 bg-neutral-100 p-2 overflow-auto max-h-full">
            <div className="flex flex-wrap lg:flex-nowrap gap-2">
                <Input.Full
                    onChange={(e) => onChange('name', e.target.value)}
                    label={_t.edit.fields.title()}
                    name="title"
                    value={selectedTask?.name || ''}
                    className="w-full lg:w-1/2 xl:w-2/3"
                    disabled={selectedTaskIndex === null}
                />
                <Input.Full
                    onChange={(e) => onChange('dueDate', e.target.value)}
                    label={_t.edit.fields.dueDate()}
                    type="date"
                    noBorder={true}
                    value={selectedTask?.dueDate || ''}
                    className="w-full lg:w-1/2 xl:w-1/3"
                    disabled={selectedTaskIndex === null}
                />
            </div>
            <HTMLEditor
                onEditorChange={(_, editor) => onChange('description', editor.getContent())}
                value={selectedTask?.description || ''}
                disabled={selectedTaskIndex === null}
            />
            {selectedTask && (
                <div className="w-full flex justify-end items-center gap-2">
                    <span className="text-sm text-neutral-600">
                        {selectedTask?.done
                            ? _t.crud.itinerary.headers.taskUndoneText()
                            : _t.crud.itinerary.headers.taskDoneText()}
                    </span>
                    {selectedTask?.done ? (
                        <Icon
                            icon="fa6-solid:xmark"
                            width="1.2rem"
                            height="1.2rem"
                            className="px-2 pt-[1px] cursor-pointer text-red-500 hover:text-red-600"
                            onClick={() => {
                                const updated = structuredClone(tasks);
                                updated[selectedTaskIndex].done = !updated[selectedTaskIndex].done;
                                setTasks(updated);
                            }}
                        />
                    ) : (
                        <Icon
                            icon="zondicons:checkmark"
                            width="1.2rem"
                            height="1.2rem"
                            className="px-2 cursor-pointer text-primary-500 hover:text-primary-600"
                            onClick={() => {
                                const updated = structuredClone(tasks);
                                updated[selectedTaskIndex].done = !updated[selectedTaskIndex].done;
                                setTasks(updated);
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
